'use strict';

/**
 * Base class for a View
 *
 * @category   View
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Backbone = require('backbone');
var ColidoModel = require('base/Colido.Model');
var ColidoItemView = require('base/Colido.ItemView');
var Marionette = require('backbone-marionette');
var Html = require('base/Colido.Html');
var Autolinker = require('autolinker');
var _ = require('underscore');

require('bootstrap-select');

//Add Colido.View stuff in before Marionette subclasses extend
_.extend(Marionette.View.prototype, {

    /**
     * Element for the listcontrol scroll handler
     * @type {jQuery}
     */
    scrollHandler: null,

    /**
     * Callback, that is applied
     * @type {function}
     */
    scrollHandlerCallback: null,

    /**
     * Selectpickers to destroy
     * @param {array}
     */
    selectPickers: [],

    /**
     * Mixin default and optional template helpers
     */
    mixinTemplateHelpers: function(target) {
        target = target || {};
        target = _.extend(target, _.result(this, 'defaultTemplateHelpers')); //custom part

        var templateHelpers = this.getOption('templateHelpers');
        if (_.isFunction(templateHelpers)) {
            templateHelpers = templateHelpers.call(this);
        }

        return _.extend(target, templateHelpers);
    },

    /**
     * Definition of template helpers
     */
    defaultTemplateHelpers: function(){
        return {
            _: _,
            app: App,
            view: this,
            model: this.model ? this.model : null,
            collection: this.collection ? this.collection : null,
            error: this.model && this.model.validationError ? this.model.validationError : {},
            user: App.user ? App.user.toJSON() : null,

            t: function() {
                return App.t.apply(App, arguments);
            },

            clog: function() {
                console.log.apply(console, arguments);
            },

            when: function(condition, output) {
                if (arguments.length === 2) {
                    return condition ? output : null;
                } else {
                    console.log('Missing parameters for "when(condition, output)".');
                }
            },

            ifelse: function(condition, output, alternative) {
                if (arguments.length === 3) {
                    return condition ? output : alternative;
                } else if (arguments.length === 2) {
                    return condition ? condition : output;
                } else {
                    console.log('Missing parameters for "ifelse(condition, output, alternative)".');
                }
            },

            /**
             * Returns the images size for the given string
             * @param {string} sizeName The size name
             * @returns {string}|{array} The image sizes as string or array
             */
            imageSize: function(sizeName) {
                return ColidoItemView.prototype.imageSizes[sizeName];
            },

            /**
             * Returns the viewport size for the given string
             * @param {string} sizeName The size name
             * @returns {string}|{array} The viewport sizes as string or array
             */
            viewportSize: function(sizeName) {
                return ColidoItemView.prototype.imageViewportSizes[sizeName];
            },

            /**
             * Returns the proper imagepath for the given imageobject and size
             * @param {object} imageObject The image/asset object containing baseUrl and fileName
             * @param {string} size        The desired size of the image
             * @returns {string} The imagepath
             */
            imagePath: function(imageObject, size) {
                size  = size || '640x640';

                return _.isObject(imageObject)
                       ? imageObject.baseUrl + size + '/' + imageObject.fileName
                       : '';
            },

            responsiveImage: function(imagePath, sizes, viewportSizes, alt, classes) {
                return Html.responsiveImage(imagePath, sizes, viewportSizes, alt, classes);
            },

            icon: function(name, size, addClasses) {
                return Html.icon(name, size, addClasses);
            },

            link: function(options) {
                return Html.link(options);
            },

            multiline: function(string) {
                return Html.multiline(string);
            },

            nl2br: function(string) {
                return Html.nl2br(string);
            },

            list: function(string, tagName) {
                return Html.list(string, tagName);
            },

            currency: function(number, currency) {
                return Html.formatCurrency(number, currency);
            },

            byte: function(number) {
                return Html.formatByte(number);
            },

            date: function(timestamp) {
                return Html.formatDate(timestamp);
            },

            time: function(timestamp) {
                return Html.formatTime(timestamp);
            },

            number: function (number) {
                return Html.formatNumber(number);
            },

            /**
             * Autolinks the given text
             * @param {String}  text    The text to autolink
             * @param {Object=} options (Optional) Options to apply for autolinking
             * @see https://github.com/gregjacobs/Autolinker.js/
             * @returns {String} Text with replaced links
             */
            autolink: function(text, options) {
                options = options || {};
                options = _.extend({
                    newWindow: true,
                    className: 'autolink',
                    phone: false,
                    twitter: false,
                    hashtag: false,
                }, options);

                return Autolinker.link(text, options);
            },
        };
    },

    /**
     * Creates a styleable selectpicker
     * @param {object|string} selector The selector or the jquery dom element
     * @param {object|null}   options  (Optional) Additional options to pass
     */
    createSelectpicker: function(selector, options) {
        var config = _.extend({
            container: 'body',
            mobile: window.browser.isMobile,
            dropupAuto: true,
        }, options || {});

        if (_.isString(selector)) {
            selector = $(selector);
        }

        if (selector.length) {
            selector.selectpicker(config);
            this.selectPickers.push(selector);
        }
    },

    /**
     * Renders the selectfield options for the given list
     * @param {DOMElement|string}         holder      The DOM Element or a selector
     * @param {Colido.Collection|array}   list        The list with the items
     * @param {string}                    key         The field to use value
     * @param {string}                    label       The field to use for description
     * @param {function}                  selected    Callback for selected items
     * @param {function}                  disabled    Callback for disabled items
     * @param {string|boolean}            placeholder Placeholder to prepend
     * @param {string|boolean}            grouping   Wraps items into an optgroup that have the same grouping
     * @returns {string} The html string of the options
     */
    renderSelectOptions: function(holder, list, key, label, selected, disabled, placeholder, grouping) {
        var markup = '';
        var selectedFound = false;

        // Do we have selector or an existing DOM Element
        if (_.isString(holder)) {
            holder = $(holder);
        }

        // Holder valid?
        if (holder.length) {
            // Remove previous
            holder.find('option,optgroup').remove();

            // Run throug list for options
            for (var i = 0; i < list.length; i++) {
                var item = list instanceof Backbone.Collection
                    ? list.at(i)
                    : list[i];

                var data = _.isObject(item)
                    ? (item instanceof ColidoModel
                       ? {value: item.get(key), label: item.get(label)}
                       : {value: item[key], label: item[label]})
                    : {value: item, label: item};

                var element = $('<option />');

                if (data.value || data.value === '') {
                    element.attr('value', data.value);
                }

                if (data.label) {
                    element.attr('label', data.label).html(data.label);
                }

                // Set selected state
                if (!selectedFound && selected && _.isFunction(selected) && selected(data, item)) {
                    selectedFound = true;
                    element.attr('selected', true);
                }

                // Set disabled state
                if (disabled && _.isFunction(disabled) && disabled(data, item)) {
                    element.attr('disabled', true);
                }

                if (grouping) {
                    var group = item instanceof ColidoModel
                              ? item.get(grouping)
                              : item[grouping];

                    if (group.length > 0) {
                        element.attr('data-group', group);
                    }
                }

                holder.append(element);
            }

            // Add placeholder?
            if (placeholder) {
                var placeholderOption = $('<option>')

                if (_.isObject(placeholder)) {
                    placeholderOption.text(placeholder.name || '').val(placeholder.value || '').attr('disabled', placeholder.disabled);
                } else {
                    placeholderOption.text(placeholder).val('').attr('disabled', true);
                }
                if (!holder.find('option[selected]').length) {
                    placeholderOption.attr('selected', true);
                }

                holder.attr('placeholder', placeholder)
                    .attr('data-placeholder', placeholder)
                    .prepend(placeholderOption);
            } else {
                holder.removeAttr('placeholder').removeAttr('data-placeholder');
            }

            // Add grouping?
            if (grouping) {
                var groups = [];
                holder.find('option[data-group]').each(function(n, el) {
                    el = $(el);
                    var group = el.data('group');
                    if (!_.contains(groups, group)) {
                        groups.push(group);
                    }
                });

                _.each(groups, function(group) {
                    var optGroup = $('<optgroup>').attr('label', group);
                    holder.find('option[data-group="' + group + '"]').wrapAll(optGroup);
                });
            }

            // Create markup for return
            markup = holder.html();
        }

        return markup;
    },

    /**
     * More convenient wrapper to call renderSelectOptions with options object
     * @param {object} options The options to use
     */
    renderSelectField: function(options) {
        options = options || {};

        // Merge options with base options
        options = _.extend({
            holder: 'select',
            list: [],
            key: 'value',
            label: 'label',
            selected: null,
            disabled: null,
            placeholder: false,
            grouping: false,
        }, options);

        return this.renderSelectOptions.apply(this, _.values(options));
    },

    /**
     * Sets the row attribute of a textarea to the number of lines of it's content
     * @param {DOMElement}|{Event}|{string} textarea  The textarea(s) to fix. Could be either a selector, DOMElement or Event like 'input'
     */
    fixTextareaSize: function(textareas) {
        //textareas might be an event.
        textareas = textareas.currentTarget ? $(textareas.currentTarget) : $(textareas);

        textareas.each(function(index, textarea) {
            textarea = $(textarea);

            textarea.attr('rows', 1); //needed so a textarea can shrink again

            var textareaContentHeight = Math.ceil(textarea[0].scrollHeight - parseFloat(textarea.css('padding-top')) - parseFloat(textarea.css('padding-bottom')));
            var textareaLineHeight = parseFloat(textarea.css('line-height'));
            var softLinebreaks = Math.round(textareaContentHeight / textareaLineHeight);
            var hardLinebreaks = (textarea.val().match(/\n/g) || []).length + 1;
            var rows = Math.max(hardLinebreaks, softLinebreaks, 1);

            textarea.attr('rows', rows);
        })
    },

    //@todo this is too specific to be in Colido.View
    /**
     * Applies scroll control to listcontrol
     * @return {void}
     */
    applyFixedListControl: function(element, scrollContainer, offsetElements) {
        if (element.length) {
            scrollContainer = scrollContainer || $('#content');

            offsetElements = offsetElements || ['.list-header-region'];
            offsetElements = _.isArray(offsetElements) ? offsetElements : [offsetElements];

            var offsetHeight = _.reduce(offsetElements, function(memo, offsetElement){
                offsetElement = scrollContainer.find(offsetElement);

                return memo + (offsetElement.length ? offsetElement.outerHeight() : 0);
            }, 0);

            if (!this.scrollHandler) {
                // Create callback for the scroll handler
                this.scrollHandlerCallback = function(event) {
                    var itemsContainer = element.closest('.container').next('.container');

                    var scrollTop = scrollContainer.scrollTop();

                    if (element.data('force-sticky')) {
                        element.addClass('is-sticky');
                    } else if (
                        this.scrollHandler &&
                        this.scrollHandler.data('enabled') &&
                        scrollTop > offsetHeight
                    ) {
                        element.addClass('is-sticky');
                        itemsContainer.css('margin-top', element.outerHeight() + 'px');
                    } else {
                        element.removeClass('is-sticky');
                        itemsContainer.css('margin-top', '');
                    }
                }.bind(this);

                this.scrollHandler = scrollContainer.on('scroll', this.scrollHandlerCallback);
                this.scrollHandler.data('enabled', true);
            }

            // Trigger manual scroll, for the case if the view is re-rendered and scroll pos > 0
            scrollContainer.trigger('scroll');
        }
    },

    disableFixedListControl: function() {
        if (this.scrollHandler) {
            this.scrollHandler.data('enabled', false);
            this.scrollHandler.trigger('scroll');
        }
    },

    enableFixedListControl: function() {
        if (this.scrollHandler) {
            this.scrollHandler.data('enabled', true);
            this.scrollHandler.trigger('scroll');
        }
    },

    onDestroy: function() { //@todo refactor this to use events. not everyone is calling super/parent implementation
        // Destroy fixed list control
        if (this.scrollHandler && this.scrollHandlerCallback) {
            this.scrollHandler.off('scroll', this.scrollHandlerCallback);
            this.scrollHandler = null;
            this.scrollHandlerCallback = null;
        }

        // Destroy selectpickers
        if (this.selectPickers.length) {
            _.each(this.selectPickers, function(object) {
                if (object.data('selectpicker')) {
                    object.off();
                    //object.data('selectpicker').destroy();
                }
            }.bind(this));

            $('body > .bootstrap-select').remove();
        }
    },

    /**
     * Retunss a rem value based of the given pixel value
     * @param {number} input The pixel value
     * @returns {number} The rem value, base on the font-size
     */
    getRemSize: function(input) {
        var fz = $('body').css('fontSize');
        var base = /px$/i.test(fz)
                 ? parseFloat(fz.replace('px', ''))
                 : $(window).width() / 100 * parseFloat(fz.replace('px', ''));

        return Math.round(input * 10 / base) / 10;
    },

    /**
     * Returns a px value based of the given rem value
     * @param {number} input The rem value
     * @returns {number} The pixel value, base on the font-size
     */
    getPxSize: function(input) {
        var fz = $('body').css('fontSize');
        var base = /px$/i.test(fz)
                 ? parseFloat(fz.replace('px', ''))
                 : $(window).width() / 100 * parseFloat(fz.replace('px', ''));

        return base * input;
    },

    /**
     * Clears a user selection
     */
    clearUserSelection: function() {
        if (window.getSelection) {
            if (window.getSelection().empty) {  // Chrome
                window.getSelection().empty();
            } else if (window.getSelection().removeAllRanges) {  // Firefox
                window.getSelection().removeAllRanges();
            }
        } else if (document.selection) {  // IE?
            document.selection.empty();
        }
    },
});